import { Component, OnDestroy, OnInit } from '@angular/core';
import { SiteService } from '@app/app-state/site.service';
import { UserService } from '@app/app-state/user.service';
import { ChartDataService } from '@app/backend/chart-data.service';
import { GenerateChartDataService } from '@app/backend/generate-chart-data.service';
import { ChartDataInterface, FielderAnalyticsInterface } from "@app/models/chart-data.model";
import * as Highcharts from 'highcharts/highstock';

import * as charts from '@utilities/highchart-objectify';
import { Subscription } from 'rxjs';
import { GraphReportComponent } from '@app/gui/report/graph-report/graph-report.component';
import { MatDialog } from '@angular/material/dialog';
import { ChartHelperService } from '@app/backend/chart-helper.service';

@Component({
    selector: 'app-utilization-on-shot',
    templateUrl: './utilization-on-shot.component.html',
    styleUrls: ['./utilization-on-shot.component.scss'],
    standalone: false
})
export class UtilizationOnShotComponent implements OnInit, OnDestroy{

	public Highcharts: typeof Highcharts = Highcharts; // required

	public chartOptions = [];
	private subscriptions: Subscription = new Subscription();

	public loading:boolean;

	private domainId: number;
	private subdomainId: number;
	private siteId: number;
	private startDate: string;
	private endDate: string;
	private selectedTimeline: string;
	public visibleDownloadMenuIndex: number | null = null;
	private fielderAnalytics:ChartDataInterface[] = [];

	constructor(
		private chartDataService:ChartDataService,
		private generateChartDataService:GenerateChartDataService,
		private siteService: SiteService,
		private userService: UserService,
		private dialog: MatDialog,
		private chartHelper: ChartHelperService,
	) {}

	ngOnInit(): void {
		this.subscriptions.add(this.chartDataService.loading$.subscribe(loading => {
			this.loading = loading;
		}));

		this.subscriptions.add(this.chartDataService.fielderAnalytics$.subscribe((res) => {
			this.triggerLoadData(res);
		}));

		this.subscriptions.add(this.siteService.domainId$.subscribe(domainId => {
			this.domainId = domainId;
			if (this.domainId) {
				this.getReport();
			}
		}));

		this.subscriptions.add(this.siteService.subDomainId$.subscribe(subdomainId => {
			this.subdomainId = subdomainId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.siteId$.subscribe(siteId => {
			this.siteId = siteId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.startDate$.subscribe(startDate => {
			this.startDate = startDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.endDate$.subscribe(endDate => {
			this.endDate = endDate;
			this.triggerLoadData();
		}));
		
		this.subscriptions.add(this.siteService.selectedTimeline$.subscribe(selectedTimeline => {
			this.selectedTimeline = selectedTimeline;
			this.triggerLoadData();
		})); 

		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe(() => {
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.selectedDrillMachine$.subscribe(() => {
			this.chartDataService.getAllFielderAnalytics();
		}));
	}

	private async getReport() {
		this.subscriptions.add(this.chartDataService.getCacheFielderAnalytics().subscribe((res) => {
			this.triggerLoadData(res);
		}))
	}

	private loadData = (chartData: ChartDataInterface[])=> {
		this.fielderAnalytics = chartData;
		const data:FielderAnalyticsInterface = this.generateChartDataService.generateChartData(this.fielderAnalytics);
		for (const key in data?.utilizationByShot) {
			this.chartOptions = charts.generateChartOptions(data?.utilizationByShot[key]);
			this.chartDataService.exportedGraphData$.next(data.utilizationByShot[key]);
		}
	}

	private triggerLoadData(data? : ChartDataInterface[]) {
		const originalFielderAnalytics = data || this.generateChartDataService.originalFielderAnalytics;
		this.fielderAnalytics = this.generateChartDataService.getFilteredData(
			originalFielderAnalytics,
			this.domainId,
			this.subdomainId,
			this.siteId,
			this.startDate,
			this.endDate,
			this.selectedTimeline
		);

		this.loadData(this.fielderAnalytics);
	}

	public pdfDownload() {
		this.dialog.open(GraphReportComponent, {
			width: '100vw', 
			height: '100vh',
			maxWidth: '100vw',
			panelClass: 'full-screen-dialog',
			disableClose: true
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	chartRefs: Highcharts.Chart[] = [];
	
	onChartInstance(chart: Highcharts.Chart, index: number): void {
		this.chartRefs[index] = chart;
	}

	toggleDownloadMenu(index: number): void {
		this.visibleDownloadMenuIndex = this.chartHelper.toggleDownloadMenu(index, this.visibleDownloadMenuIndex);
	}

	downloadCSV(index: number): void {
		this.chartHelper.downloadCSV(this.chartRefs[index]);
		this.visibleDownloadMenuIndex = null;
	}

	downloadXLS(index: number): void {
		this.chartHelper.downloadXLS(this.chartRefs[index]);
		this.visibleDownloadMenuIndex = null;
	}

	onFullScreenClick(index: number): void {
		this.chartHelper.toggleFullScreen(this.chartRefs[index]);
	}

	onTableViewClick(index: number): void {
		const chart = this.chartRefs[index];
		if (chart) {
			this.chartHelper.viewChartData(chart, index);
		}
	}
}
